import { Check, Copy, KeyRound } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { useTranslations } from "@/shared/lang/useTranslations";

export interface WebhookSecretBannerProps {
  secret: string;
  /** The header the receiver must compare it against — `x-secret`. */
  headerName: string;
  onDismiss: () => void;
}

/**
 * The secret, shown once.
 *
 * It is returned by the create and rotate calls and by nothing else — every later read is masked and there is
 * no endpoint to recover it. So it renders **on the page**, not inside the dialog that produced it: an
 * overlay closes on Escape or a stray click, and dismissing it before copying would destroy the credential.
 * Here it stays until the reader says they have saved it. Same posture as the API-key banner.
 */
export function WebhookSecretBanner({ secret, headerName, onDismiss }: WebhookSecretBannerProps) {
  const { t } = useTranslations("settings");
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(secret)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  return (
    <ColocaAlert variant="warning" icon={KeyRound} heading={t("webhooks_secret_banner_title")}>
      <p className="text-[12px] text-muted-foreground">
        {t("webhooks_secret_banner_warning", { header: headerName })}
      </p>
      <div className="mt-2 flex items-center gap-2">
        <code className="min-w-0 flex-1 overflow-x-auto rounded bg-muted px-2.5 py-2 font-mono text-[12px] text-foreground">
          {secret}
        </code>
        <ColocaButton variant="default" size="xs" icon={copied ? Check : Copy} onClick={handleCopy}>
          {copied ? t("webhooks_secret_copied") : t("webhooks_secret_copy")}
        </ColocaButton>
      </div>
      <ColocaButton variant="ghost" size="xs" className="mt-2" onClick={onDismiss}>
        {t("webhooks_secret_dismiss")}
      </ColocaButton>
    </ColocaAlert>
  );
}
